<template>
  <ListDailyScene @detail="detailScene" :selected="selected" />

  <div id="map-scene" class="w-full h-[98vh]"></div>

  <TimelineWithPagination
    v-if="!bykodepetak.loading"
    v-model:page="page"
    :minPage="minPage"
    :maxPage="maxPage"
    :currentMonth="currentMonth"
    :dates="tgl"
    :selectedDate="selected"
    @onSelectChange="changePage"
    @onClickTime="clickTime"
  />

  <div class="absolute z-10 top-5 right-5" v-if="isnull">
    <AlertErrorComponent message="No scene data on this date" />
  </div>
</template>

<script>
import L from "leaflet";
import { ref, onMounted } from "vue";
import ListDailyScene from "./ListDailyScene.vue";
import moment from "moment";
import { useSceneStore, usesgnksoFeaturesStore } from "@/store";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import AlertErrorComponent from "@/components/AlertErrorComponent.vue";
import TimelineWithPagination from "@/components/TimelineWithPagination.vue";

export default {
  components: {
    ListDailyScene,
    AlertErrorComponent,
    TimelineWithPagination,
  },
  setup() {
    const route = useRoute();
    const sceneStore = useSceneStore();
    const { bykodepetak, dates, currentMonth, minPage, maxPage, tgl, page } = storeToRefs(
      sceneStore
    );
    const sgnksoStore = usesgnksoFeaturesStore();
    const { sgnbykodepetak } = storeToRefs(sgnksoStore);
    const kodepetak = route.params.kode_petak;

    const mapscene = ref();
    const center = ref([-1.476796, 121.001893]);
    const zoom = ref(5);
    const sceneLayer = ref(null);

    const selected = ref(null);
    const isnull = ref(false);
    const currentYear = new Date().getFullYear();

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const createMaps = () => {
      mapscene.value = L.map("map-scene", { zoomAnimation: false }).setView(
        center.value,
        zoom.value
      );
      L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png", {
        minZoom: 5,
        attribution: "Citra Satelite © 2025",
      }).addTo(mapscene.value);
    };

    onMounted(async () => {
      await createMaps();
    });

    const onEachFeature = async (feature, layer) => {
      if (feature.properties) {
        const div = document.createElement("div");

        const info = document.createElement("div");

        const table = document.createElement("table");
        info.appendChild(table);

        for (var key in feature.properties) {
          const row = document.createElement("tr");
          table.appendChild(row);

          const cell1 = document.createElement("td");
          cell1.setAttribute("class", "align-text-top");
          const cell2 = document.createElement("td");
          cell2.setAttribute("class", "px-2 align-text-top");
          const cell3 = document.createElement("td");

          const textNode1 = document.createTextNode(key);
          const textNode2 = document.createTextNode(":");
          const textNode3 = document.createTextNode(feature.properties[key]);
          cell1.appendChild(textNode1);
          cell2.appendChild(textNode2);
          cell3.appendChild(textNode3);
          row.appendChild(cell1);
          row.appendChild(cell2);
          row.appendChild(cell3);
        }

        div.appendChild(info);
        layer.bindPopup(div, {
          maxHeight: 300,
        });
      }
    };

    const detailScene = async (val) => {
      selected.value = val.tanggal;
      const selectedmonth = new Date(val.tanggal).getMonth() + 1;
      const selectedyear = new Date(val.tanggal).getFullYear();
      sceneStore.currentMonth = monthNames[selectedmonth - 1];

      if (page.value != selectedmonth) {
        page.value = selectedmonth;
        sceneStore.tgl = [];

        const key = `${selectedyear}-${selectedmonth}`;

        const groupedByMonth = dates.value.reduce((acc, date) => {
          const month = new Date(date).getMonth();
          const monthKey = `${selectedyear}-${month + 1}`;

          if (!acc[monthKey]) {
            acc[monthKey] = [];
          }
          acc[monthKey].push(date);
          acc[monthKey].sort((a, b) => new Date(a) - new Date(b));
          return acc;
        }, {});

        sceneStore.tgl = groupedByMonth[key].map((x) => moment(x).format("MM/DD"));
      }

      await sgnksoStore.getByDate(kodepetak, selected.value);

      if (mapscene.value.hasLayer(sceneLayer.value)) {
        await mapscene.value.removeLayer(sceneLayer.value);
      }

      const geojson =
        sgnksoStore.featurebydate.rows?.length != 0
          ? sgnksoStore.featurebydate.rows[0]
          : [];

      if (geojson?.length != 0) {
        isnull.value = false;
        sceneLayer.value = new L.Shapefile(geojson, {
          onEachFeature: onEachFeature,
        });
        mapscene.value.fitBounds(sceneLayer.value.getBounds());
        sceneLayer.value.addTo(mapscene.value);
      } else {
        isnull.value = true;
      }
    };

    const date = (date) => {
      return moment(date).format("DD/MM/YY");
    };

    const changePage = (val) => {
      sceneStore.tgl = [];
      page.value = val;
      sceneStore.currentMonth = monthNames[val - 1];

      const key = `${currentYear}-${val}`;

      const groupedByMonth = dates.value.reduce((acc, date) => {
        const year = new Date(date).getFullYear();
        const month = new Date(date).getMonth();
        const monthKey = `${year}-${month + 1}`;

        if (!acc[monthKey]) {
          acc[monthKey] = [];
        }
        acc[monthKey].push(date);
        acc[monthKey].sort((a, b) => new Date(a) - new Date(b));
        return acc;
      }, {});

      sceneStore.tgl = groupedByMonth[key].map((x) => moment(x).format("MM/DD"));
    };

    const clickTime = async (val) => {
      selected.value = currentYear + "-" + val.replace(/\//g, "-");

      await sgnksoStore.getByDate(kodepetak, selected.value);

      if (mapscene.value.hasLayer(sceneLayer.value)) {
        await mapscene.value.removeLayer(sceneLayer.value);
      }

      const geojson =
        sgnksoStore.featurebydate.rows?.length != 0
          ? sgnksoStore.featurebydate.rows[0]
          : [];

      if (geojson?.length != 0) {
        isnull.value = false;
        sceneLayer.value = new L.Shapefile(geojson, {
          onEachFeature: onEachFeature,
        });
        mapscene.value.fitBounds(sceneLayer.value.getBounds());
        sceneLayer.value.addTo(mapscene.value);
      } else {
        isnull.value = true;
      }
    };

    return {
      detailScene,
      selected,
      date,
      changePage,
      page,
      clickTime,
      isnull,
      currentMonth,
      minPage,
      maxPage,
      tgl,
      bykodepetak,
      sgnbykodepetak,
    };
  },
};
</script>

<style>
#map-scene {
  z-index: 1;
}
</style>
